import React, { useContext, useRef, useState } from "react";
import {
  Button as AntdButton,
  Col,
  Row,
  ButtonProps as AntdButtonProps,
  message,
  Select,
  Radio,
  Spin,
  DatePicker,
  Input as AntdInput,
} from "antd";
import dayjs from "dayjs";
import { IoIosAdd } from "react-icons/io";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import {
  useForm,
  FormProvider,
  Controller as RHFController,
} from "react-hook-form";
import Controller from "../../../form-components/controller";
import Input from "../../../form-components/input";
import { IQuantity } from "../../../../models/product/response";
import EndPoints from "../../../../services/end-points";
import { IBatch } from "../../../../models/wms/batch/response";
import { IUpdateBatch } from "../../../../models/wms/batch/request";
import WmsStorablePicker, {
  IStorableSelection,
} from "../../../wms/wms-storable-picker";
import { formatBatchPurchasePriceOf } from "../../../../utils/helpers/batch-purchase-price";
import { isValidPurchaseDate } from "../../../../utils/helpers/batch-date";

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
  productName?: string;
  soldByUnit?: boolean;
}

// Helper function to get display name from store_name
const getStoreName = (name: string | { ar: string; en: string }): string => {
  if (typeof name === "string") return name;
  return name?.ar || name?.en || "-";
};

interface IFormValues {
  storeId: number | null;
  mode: "existing" | "new";
  // existing batch
  batchId: number | null;
  addedQuantity: number;
  /** Blank leaves the stored cost alone; the existing-batch mode corrects a
   *  typo, so a price is only sent when someone actually retypes it. */
  correctedPurchasePrice: string;
  // new batch
  enteredQuantity: number;
  purchasePrice: string;
  batchNumber: string;
  expirationDate: string;
  purchaseDate: string;
  notes: string;
  storableType?: IStorableSelection["storableType"];
  storableId?: IStorableSelection["storableId"];
}

const AddQuantityButton: React.FC<ButtonProps> = ({
  passedId,
  quantities,
  productName,
  soldByUnit,
  ...props
}) => {
  const { t } = useTranslation();
  const { actions, loading } = useContext(ProductContext);
  const storableRef = useRef<IStorableSelection | undefined>(undefined);

  const [showFormModal, hideFormModal] = useModal(() => {
    const methods = useForm<IFormValues>({
      defaultValues: {
        storeId: null,
        mode: "new",
        batchId: null,
        addedQuantity: 1,
        correctedPurchasePrice: "",
        enteredQuantity: 1,
        purchasePrice: "",
        batchNumber: "",
        expirationDate: "",
        purchaseDate: dayjs().format("YYYY-MM-DD"),
        notes: "",
      },
    });

    const { control, watch, setValue } = methods as any;
    const [submitting, setSubmitting] = useState(false);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [existingBatches, setExistingBatches] = useState<IBatch[]>([]);
    const setStorable = (val: IStorableSelection | undefined) => {
      storableRef.current = val;
      setValue("storableType", val?.storableType ?? undefined, {
        shouldValidate: true,
      });
      setValue("storableId", val?.storableId ?? undefined, {
        shouldValidate: true,
      });
    };

    const selectedStoreId = watch("storeId");
    const mode = watch("mode");
    const selectedBatchId = watch("batchId");
    const selectedBatch = existingBatches.find(
      (batch) => batch.id === selectedBatchId,
    );

    const handleStoreChange = async (storeId: number) => {
      setValue("storeId", storeId);
      setValue("batchId", null);
      setValue("correctedPurchasePrice", "");
      setExistingBatches([]);
      storableRef.current = undefined;
      setValue("storableType", undefined);
      setValue("storableId", undefined);
      if (storeId) {
        setLoadingBatches(true);
        try {
          const { data } = await EndPoints.wmsBatch.getAllBatches({
            productId: passedId,
            storeId,
            perPage: -1,
          });
          setExistingBatches(data?.data ?? []);
        } catch (error: any) {
          setExistingBatches([]);
        } finally {
          setLoadingBatches(false);
        }
      }
    };

    const handleConfirm = async () => {
      const isValid = await methods.trigger();
      if (!isValid) return;

      const data = methods.getValues();

      if (!data.storeId) {
        message.error(t("please_select_store"));
        return;
      }

      setSubmitting(true);
      try {
        if (data.mode === "existing") {
          if (!data.batchId) {
            message.error(t("please_select_batch"));
            setSubmitting(false);
            return;
          }
          const qty = Number(data.addedQuantity);
          if (!qty || qty < 1) {
            message.error(t("must_be_zero_or_positive"));
            setSubmitting(false);
            return;
          }
          const batch = existingBatches.find((b) => b.id === data.batchId);
          if (batch) {
            const payload: IUpdateBatch = {
              enteredQuantity: batch.enteredQuantity + qty,
            };

            // The stamped currency can never be corrected, only the amount,
            // and an untouched field must not overwrite the stored cost.
            const correctedPrice = String(
              data.correctedPurchasePrice ?? "",
            ).trim();
            if (correctedPrice !== "") {
              const price = Number(correctedPrice);
              if (!Number.isFinite(price) || price < 0) {
                message.error(t("must_be_zero_or_positive"));
                setSubmitting(false);
                return;
              }
              payload.purchasePrice = price;
            }

            await EndPoints.wmsBatch.updateBatch(data.batchId, payload);
          }
        } else {
          if (!data.storableType || !data.storableId) {
            message.error(t("please_select_storage_location"));
            setSubmitting(false);
            return;
          }
          if (!isValidPurchaseDate(data.purchaseDate)) {
            message.error(t("purchase_date_invalid"));
            setSubmitting(false);
            return;
          }
          if (!data.expirationDate) {
            message.error(t("expiration_date_required"));
            setSubmitting(false);
            return;
          }
          const qty = Number(data.enteredQuantity);
          if (!qty || qty < 1) {
            message.error(t("must_be_zero_or_positive"));
            setSubmitting(false);
            return;
          }
          // The forecast report prices an order line from the batch it would
          // be bought against, so a batch without a cost is worth nothing to
          // it — the server refuses one too.
          const rawPurchasePrice = String(data.purchasePrice ?? "").trim();
          const purchasePrice = Number(rawPurchasePrice);
          if (
            rawPurchasePrice === "" ||
            !Number.isFinite(purchasePrice) ||
            purchasePrice < 0
          ) {
            message.error(t("batch_purchase_price_required"));
            setSubmitting(false);
            return;
          }
          await EndPoints.wmsBatch.createBatch({
            productId: passedId,
            storeId: data.storeId,
            enteredQuantity: qty,
            purchasePrice,
            batchNumber: data.batchNumber || undefined,
            expirationDate: data.expirationDate,
            purchaseDate: data.purchaseDate,
            notes: data.notes || undefined,
            storableType: data.storableType,
            storableId: data.storableId,
          });
        }
        actions.getData();
        hideFormModal();
      } catch (error: any) {
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <FormHookModal
        id="add-quantity-modal"
        confirmButtonText={t("add")}
        loading={submitting || loading.includes("addQuantity")}
        title={t("add_quantity")}
        subtitle={
          productName ? `${t("product_name")}: ${productName}` : undefined
        }
        hideModal={hideFormModal}
        width="min(820px, 92vw)"
        bodyScroll
        style={{ marginTop: "1.6rem", maxHeight: "85vh" }}
        onSubmit={() => {}}
        confirmClick={handleConfirm}
      >
        <FormProvider {...methods}>
          <Row gutter={[16, 12]} style={{ margin: 0, width: "100%" }}>
            {/* Store selector */}
            <Col span={24}>
              <FormItem label={t("store")}>
                <RHFController
                  name="storeId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      style={{ width: "100%" }}
                      value={field.value}
                      onChange={(val) => handleStoreChange(val)}
                      options={quantities.map((q) => ({
                        value: q.store_id,
                        label: getStoreName(q.store_name),
                      }))}
                    />
                  )}
                />
              </FormItem>
            </Col>

            {/* Mode toggle */}
            {selectedStoreId && (
              <Col span={24}>
                <RHFController
                  name="mode"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setValue("batchId", null);
                        setValue("correctedPurchasePrice", "");
                        storableRef.current = undefined;
                        setValue("storableType", undefined);
                        setValue("storableId", undefined);
                      }}
                    >
                      <Radio value="new">{t("new_batch")}</Radio>
                      <Radio value="existing">{t("existing_batch")}</Radio>
                    </Radio.Group>
                  )}
                />
              </Col>
            )}

            {/* Existing batch mode */}
            {selectedStoreId && mode === "existing" && (
              <>
                <Col span={24}>
                  <FormItem label={t("select_batch")}>
                    {loadingBatches ? (
                      <Spin size="small" />
                    ) : (
                      <RHFController
                        name="batchId"
                        control={control}
                        rules={{ required: mode === "existing" }}
                        render={({ field }) => (
                          <Select
                            style={{ width: "100%" }}
                            value={field.value}
                            onChange={(value) => {
                              // A correction belongs to the batch it was typed
                              // for, so switching batches drops it.
                              field.onChange(value);
                              setValue("correctedPurchasePrice", "");
                            }}
                            options={existingBatches.map((b) => ({
                              value: b.id,
                              // The added quantity inherits this batch's cost,
                              // so it is shown before the choice is made.
                              label: b.expirationDate
                                ? `${b.batchNumber ?? "#" + b.id} — ${b.expirationDate} (${t("available")}: ${b.availableQuantity}) — ${t("batch_purchase_price")}: ${formatBatchPurchasePriceOf(b, t("currency"))}`
                                : `${b.batchNumber ?? "#" + b.id} — (${t("batch_has_no_expiration_date")} — (${t("available")}: ${b.availableQuantity}) — ${t("batch_purchase_price")}: ${formatBatchPurchasePriceOf(b, t("currency"))}`,
                            }))}
                          />
                        )}
                      />
                    )}
                  </FormItem>
                </Col>
                <Col span={24}>
                  <FormItem
                    label={t(
                      soldByUnit
                        ? "added_quantity_grams"
                        : "added_quantity_pieces",
                    )}
                  >
                    <Controller
                      name="addedQuantity"
                      control={control}
                      rules={{
                        validate: (v) =>
                          Number(v) >= 1 || t("must_be_zero_or_positive"),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                {selectedBatch && (
                  <Col span={24}>
                    <FormItem label={t("purchase_date")}>
                      {selectedBatch.purchaseDate}
                    </FormItem>
                    <FormItem label={t("correct_batch_purchase_price")}>
                      <Controller
                        name="correctedPurchasePrice"
                        control={control}
                        rules={{
                          validate: (v: any) =>
                            String(v ?? "").trim() === "" ||
                            Number(v) >= 0 ||
                            t("must_be_zero_or_positive"),
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="number"
                            placeholder={t("leave_empty_to_keep_current_value")}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                        {`${t("batch_purchase_price_current")}: ${formatBatchPurchasePriceOf(selectedBatch, t("currency"))} — ${t("batch_purchase_price_currency_locked")}`}
                      </div>
                    </FormItem>
                  </Col>
                )}
              </>
            )}

            {/* New batch mode */}
            {selectedStoreId && mode === "new" && (
              <>
                <Col xs={24} md={12}>
                  <FormItem
                    label={t(
                      soldByUnit
                        ? "entered_quantity_grams"
                        : "entered_quantity_pieces",
                    )}
                  >
                    <Controller
                      name="enteredQuantity"
                      control={control}
                      rules={{
                        validate: (v) =>
                          Number(v) >= 1 || t("must_be_zero_or_positive"),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={12}>
                  <FormItem
                    label={t("batch_purchase_price_selling_unit")}
                    required
                  >
                    <Controller
                      name="purchasePrice"
                      control={control}
                      rules={{
                        validate: (v: any) =>
                          (String(v ?? "").trim() !== "" && Number(v) >= 0) ||
                          t("batch_purchase_price_required"),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                      {t("batch_purchase_price_hint")}
                    </div>
                  </FormItem>
                </Col>
                <Col xs={24} md={12}>
                  <FormItem label={t("purchase_date")} required>
                    <Controller
                      name="purchaseDate"
                      control={control}
                      rules={{
                        validate: (value) =>
                          mode !== "new" ||
                          isValidPurchaseDate(value) ||
                          t("purchase_date_invalid"),
                      }}
                      render={({ field }) => (
                        <DatePicker
                          name={field.name}
                          ref={field.ref}
                          onBlur={field.onBlur}
                          value={field.value ? dayjs(field.value) : null}
                          format="YYYY-MM-DD"
                          placeholder="YYYY-MM-DD"
                          disabledDate={(date) => date.isAfter(dayjs(), "day")}
                          style={{ width: "100%", direction: "ltr" }}
                          onChange={(_date, dateString) => field.onChange(dateString)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={12}>
                  <FormItem label={t("expiration_date")}>
                    <Controller
                      name="expirationDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          name={field.name}
                          ref={field.ref}
                          onBlur={field.onBlur}
                          value={field.value ? dayjs(field.value) : null}
                          format="YYYY-MM-DD"
                          placeholder="YYYY-MM-DD"
                          style={{ width: "100%", direction: "ltr" }}
                          onChange={(_date, dateString) => field.onChange(dateString)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                <Col span={24}>
                  <FormItem label={t("note")}>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <AntdInput.TextArea
                          {...field}
                          autoSize={{ minRows: 3, maxRows: 8 }}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                <Col span={24}>
                  <div style={{ marginBottom: 6, fontWeight: 500 }}>
                    {t("storage_location")}
                  </div>
                  <WmsStorablePicker
                    storeId={selectedStoreId}
                    onStorableSelected={setStorable}
                  />
                </Col>
              </>
            )}
          </Row>
        </FormProvider>
      </FormHookModal>
    );
  }, [passedId, quantities, productName, soldByUnit, loading]);

  return (
    <AntdButton
      type="primary"
      icon={<IoIosAdd />}
      onClick={() => {
        storableRef.current = undefined;
        showFormModal();
      }}
      {...props}
    />
  );
};

export default AddQuantityButton;
